import React, { useState, useEffect, useMemo } from 'react'; 
import { useNavigate } from "react-router-dom"; 
import { useAuth } from '../context/AuthContext'; 
import './../styles/dashboard.css'; 

// ========================================================================= 
// Fonction d'aide pour mélanger un tableau (Fisher-Yates) 
// ========================================================================= 
const shuffleArray = (array) => { 
    const newArray = [...array]; 
    for (let i = newArray.length - 1; i > 0; i--) { 
        const j = Math.floor(Math.random() * (i + 1)); 
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]]; 
    } 
    return newArray; 
}; 

// ========================================================================= 
// Composant de l'allocation des joueurs 
// Gère l'affichage détaillé de la précommande.
// ========================================================================= 
const PlayerAllocation = ({ purchase, userData }) => { 
    // État pour les joueurs. Chaque joueur a maintenant un email et des statuts. 
    const [players, setPlayers] = useState([]); 
    const [isSharing, setIsSharing] = useState(false); 
    const [sendAllStatus, setSendAllStatus] = useState('idle'); // 'idle', 'sending', 'sent', 'error' 
    
    // NOUVEL ÉTAT : Nombre de joueurs sélectionné 
    const [selectedPlayerCount, setSelectedPlayerCount] = useState(0);  

    // Déterminer le nombre max de personnages et les limites de joueurs 
    const { allCharacters, minPlayers, maxPlayers, isPreOrder } = useMemo(() => { 
        let charactersArray = [];
        try { 
            // Assurez-vous que 'characters' existe et est un JSON valide 
            charactersArray = purchase && purchase.characters ? JSON.parse(purchase.characters) : []; 
        } catch (error) { 
            console.error('Failed to parse characters JSON:', error); 
        } 
        
        // On suppose que les propriétés min_players et max_players sont aussi dans l'objet purchase 
        const minP = parseInt(purchase?.min_players) || 0; 
        const maxP = parseInt(purchase?.max_players) || 0; 

        // VÉRIFICATION DE PRÉCOMMANDE
        const preOrder = minP === 0 && maxP === 0;

        const defaultMin = 4; // Valeur par défaut si non spécifié 
        const defaultMax = charactersArray.length; 

        let finalMin, finalMax;

        if (preOrder) { 
            finalMin = 0;
            finalMax = 0;
        } else {
            // Logique standard si ce n'est PAS une précommande
            const safeMinP = minP || defaultMin;
            const safeMaxP = safeMinP > 0 ? maxP || defaultMax : defaultMax; // Assurer que maxP a une valeur raisonnable
            
            // Le nombre de personnages disponibles pour l'affectation est le plus petit entre maxPlayers et le nombre de caractères. 
            finalMax = Math.min(safeMaxP, charactersArray.length); 
            finalMin = Math.max(Math.min(safeMinP, finalMax), 1); // La min est au moins 1 
        }

        return { 
            allCharacters: charactersArray, 
            minPlayers: finalMin, 
            maxPlayers: finalMax,
            isPreOrder: preOrder
        }; 
    }, [purchase]); 


    // Effet pour initialiser les joueurs lors du montage du composant OU lorsque le nombre de joueurs change 
    useEffect(() => { 
        // VÉRIFICATION: Ne pas allouer si c'est une précommande
        if (isPreOrder) {
            setPlayers([]); 
            setSelectedPlayerCount(0);
            return;
        }

        if (allCharacters.length > 0) { 
            
            // 1. Initialise le nombre de joueurs au minimum si ce n'est pas encore fait 
            if (selectedPlayerCount === 0 && minPlayers > 0) { 
                 setSelectedPlayerCount(minPlayers); 
                 return;  
            } 
            
            // 2. COUPE : Prend seulement les 'selectedPlayerCount' premiers personnages de la liste de l'API. 
            const charactersToUse = allCharacters.slice(1, selectedPlayerCount + 1); 

            // 3. MÉLANGE : Mélange cette liste de personnages. 
            const shuffledCharacters = shuffleArray(charactersToUse); 
            shuffledCharacters.unshift(allCharacters[0]); // Ajout du livret Organisateur 


            // 4. ALOCATION : Crée le tableau de joueurs avec l'ordre mélangé. 
            const initialPlayers = shuffledCharacters.map((characterName, index) => { 
                const existingPlayer = players[index] || {}; 

                return { 
                    email: index === 0 || index === 1 ? userData.email : (existingPlayer.email || ''),  
                    character: characterName, 
                    isSaved: index === 0 || index === 1, 
                    isSent: existingPlayer.isSent || false 
                }; 
            }); 
            
            setPlayers(initialPlayers); 
        } else { 
            setPlayers([]); 
        } 
        
    }, [allCharacters, selectedPlayerCount, userData, minPlayers, isPreOrder]); 
    
    // Gère le changement du nombre de joueurs 
    const handlePlayerCountChange = (event) => { 
        const newCount = parseInt(event.target.value, 10); 
        setSelectedPlayerCount(newCount); 
    }; 


    // Gère la mise à jour des champs de formulaire 
    const handlePlayerChange = (index, value) => { 
        const updatedPlayers = [...players]; 
        updatedPlayers[index].email = value; 
        updatedPlayers[index].isSaved = false;  
        setPlayers(updatedPlayers); 
    }; 

    // Gère le clic sur le bouton "Sauvegarder" 
    const handleSaveClick = (index) => { 
        const updatedPlayers = [...players]; 
        updatedPlayers[index].isSaved = true; 
        setPlayers(updatedPlayers); 
        console.log(`Email pour ${updatedPlayers[index].character} sauvegardé : ${updatedPlayers[index].email}`); 
    }; 

    // Gère l'envoi de TOUS les livrets 
    const handleSendAllClick = async () => { 
        setIsSharing(true); 
        setSendAllStatus('sending'); 

        // Logique d'envoi... (inchangée pour la clarté)
        for (let i = 0; i < players.length; i++) { 
            const player = players[i]; 
            if (player.email) { 
                console.log(`Envoi du livret du jeu ${purchase.id} de ${player.character} à ${player.email}. Details ${player}`); 
                const API_URL = process.env.REACT_APP_API_URL; 

                try { 
                    const token = userData.token || localStorage.getItem('token'); 
                    const response = await fetch(`${API_URL}/booklets/send-booklet-link.php?character=${player.character}&email=${player.email}&purchaseId=${purchase.id}`, { 
                        headers: { 
                            'Authorization': `Bearer ${token}`, 
                            'Content-Type': 'application/json', 
                        }, 
                    }); 
                    if (!response.ok) { 
                        console.error(`Failed to send booklet for ${player.character}`); 
                    } 
                } catch (error) { 
                    console.error('Error sending booklet:', error); 
                }  
                
                const updatedPlayers = [...players]; 
                updatedPlayers[i].isSent = true; 
                setPlayers(updatedPlayers); 
            } 
        } 

        setIsSharing(false); 
        setSendAllStatus('sent'); 
    }; 

    // Liste des options pour le sélecteur de joueurs 
    const playerCountOptions = useMemo(() => { 
        const options = []; 
        for (let i = minPlayers; i <= maxPlayers; i++) { 
            options.push(i); 
        } 
        return options; 
    }, [minPlayers, maxPlayers]); 

    const isSendAllButtonDisabled = isSharing || players.every(p => !p.email); 

    // RENDU CONDITIONNEL POUR LA PRÉCOMMANDE
    if (isPreOrder) {
        return (
            <div className="player-allocation-section pre-order-state">
                <h3>Précommande : </h3>
                <p>
                    Ce jeu est actuellement en précommande. Les livrets de personnage ne sont pas encore disponibles.
                </p>
                <p>
                    Vous serez averti par e-mail de la disponibilité du jeu et des livrets.
                    Merci de votre soutien et de votre patience !
                </p>
            </div>
        );
    }

    return ( 
        <div className="player-allocation-section"> 
            <h3>Allouer les personnages</h3> 

            {/* SÉLECTEUR DU NOMBRE DE JOUEURS */} 
            <div className="player-count-selector"> 
                <label htmlFor="player-count">Nombre de joueurs :</label> 
                <select  
                    id="player-count" 
                    className="player-count-select"  
                    value={selectedPlayerCount} 
                    onChange={handlePlayerCountChange} 
                    disabled={isSharing || playerCountOptions.length <= 1} 
                > 
                    {playerCountOptions.map(count => ( 
                        <option key={count} value={count}> 
                            {count} joueurs 
                        </option> 
                    ))} 
                </select> 
            </div> 
            
            <p>Veuillez entrer les e-mails de vos joueurs après avoir sélectionné le nombre de participants.</p> 
            
            {/* Affichage des formulaires d'e-mail */} 
            {players.map((player, index) => ( 
                <div key={index} className="player-form-card"> 
                    <div className="player-form-header"> 
                        <h4>{player.character}</h4> 
                    </div> 
                    <div className="player-form-body"> 
                        <div className="email-input-group"> 
                            <input 
                                type="email" 
                                value={player.email} 
                                onChange={(e) => handlePlayerChange(index, e.target.value)} 
                                placeholder="E-mail du joueur" 
                                required 
                                disabled={isSharing} 
                            /> 
                            {false && ( 
                                <button 
                                    className={`save-button ${player.isSaved ? 'saved' : ''}`} 
                                    onClick={() => handleSaveClick(index)} 
                                    disabled={!player.email || player.isSaved || isSharing}  
                                    title="Sauvegarder l'e-mail" 
                                > 
                                    <i className="fas fa-save"></i> 
                                </button> 
                            )} 
                        </div> 
                        {player.isSent && <p className="send-status-success">Envoyé !</p>} 
                    </div> 
                </div> 
            ))} 
            
            {/* Bouton d'envoi général */} 
            <button  
                className={`send-all-button ${isSharing ? 'sending' : ''} ${sendAllStatus === 'sent' ? 'sent' : ''}`} 
                onClick={handleSendAllClick} 
                disabled={isSendAllButtonDisabled} 
            > 
                {sendAllStatus === 'sending' && ( 
                    <i className="fas fa-spinner fa-spin"></i> 
                )} 
                {sendAllStatus === 'sent' && ( 
                    <i className="fas fa-check"></i> 
                )} 
                {sendAllStatus === 'idle' && ( 
                    <> 
                        <i className="fas fa-paper-plane"></i> 
                        <span>Envoyer tous les livrets</span> 
                    </> 
                )} 
                {sendAllStatus === 'error' && ( 
                    <> 
                        <i className="fas fa-times"></i> 
                        <span>Erreur d'envoi</span> 
                    </> 
                )} 
            </button> 
        </div> 
    ); 
}; 

// ========================================================================= 
// Composant principal du Dashboard 
// Gère l'affichage du bouton de l'achat.
// ========================================================================= 
function Dashboard() { 
    const { user, isAuthenticated } = useAuth(); 
    const [userDashboardData, setUserDashboardData] = useState(null); 
    const [userPurchases, setUserPurchases] = useState([]); 
    const [loading, setLoading] = useState(true); 
    const [activePurchaseId, setActivePurchaseId] = useState(null); 
    const navigate = useNavigate(); 

    const API_URL = process.env.REACT_APP_API_URL; 

    // ... (fetchUserData et fetchUserPurchases inchangés)
    const fetchUserData = async () => { 
        if (!isAuthenticated || !user) return;
        setLoading(true);

        try {
            const token = user.token || localStorage.getItem('token');
            const response = await fetch(`${API_URL}/users/get-user.php?id=${user.userId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setUserDashboardData(data.user);
                fetchUserPurchases(data.user.userId);
            } else {
                console.error('Failed to fetch user data');
                setUserDashboardData(null);
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            setUserDashboardData(null);
        } finally {
            setLoading(false);
        }
    }; 
    const fetchUserPurchases = async (userId) => { 
        try {
            const token = user.token || localStorage.getItem('token');

            const response = await fetch(`${API_URL}/users/get-purchases.php?userId=${user.userId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setUserPurchases(data.purchases || []);
            } else {
                console.error('Failed to fetch user purchases');
            }
        } catch (error) {
            console.error('Error fetching user purchases:', error);
        }
     }; 

    // Déclenche la récupération des données au chargement 
    useEffect(() => { 
        fetchUserData(); 
    }, [isAuthenticated, user]); 

    // Affiche un message de chargement ou d'erreur 
    if (loading) { 
        return <div className="loading-message">Chargement du dashboard...</div>; 
    } 

    if (!userDashboardData) { 
        return <div className="error-message">Veuillez vous connecter pour voir le dashboard.</div>; 
    } 

    // Gère le basculement d'affichage pour un achat donné 
    const toggleAllocation = (purchaseId) => { 
        setActivePurchaseId(activePurchaseId === purchaseId ? null : purchaseId); 
    }; 
    
    // FONCTION D'AIDE POUR LE DASHBOARD : VÉRIFIER LA PRÉCOMMANDE
    const isPurchasePreOrder = (purchase) => {
        const minP = parseInt(purchase?.min_players) || 0;
        const maxP = parseInt(purchase?.max_players) || 0;
        return minP === 0 && maxP === 0;
    };
    
    // Rendu du dashboard 
    return ( 
        <div className="dashboard-container"> 
            <div className="dashboard-card"> 
                <div className="dashboard-header"> 
                    <h1 className="dashboard-title">Dashboard</h1> 
                    <p className="dashboard-welcome"> 
                        Bienvenue, <span className="dashboard-user-name">{userDashboardData.first_name}</span>! 
                    </p> 
                </div> 
                
                <div className="purchases-section"> 
                    <h2>Mes Murder Parties</h2> 
                    {userPurchases.length === 0 ? ( 
                        <> 
                            <p>Vous n'avez pas encore acheté de murder party.</p> 
                            <button className="cta-button" onClick={() => navigate("/store")}> 
                                Découvrir la boutique 
                            </button> 
                        </> 
                    ) : ( 
                        <div className="purchases-list"> 
                            {userPurchases.map((purchase) => {
                                const isPreOrder = isPurchasePreOrder(purchase); const buttonText = isPreOrder ? 'Voir la précommande' : activePurchaseId === purchase.id ? 'Masquer les livrets' : 'Gérer les livrets';

                                return (
                                    <div key={purchase.id}  className="purchase-container"> 
                                        <div className="purchase-item"> 
    
                                            <img src={purchase.image_url} alt={purchase.product_name} className="purchase-image" /> 
                                            <div className="purchase-details"> 
                                                <h3>{purchase.product_name} 
                                                    {isPreOrder && <span className="pre-order-tag"></span>} 
                                                </h3> 
                                                <p>{purchase.description}</p> 
                                                <p>Acheté le : {new Date(purchase.purchase_date).toLocaleDateString()}</p> 
                                                
                                                <button  
                                                    className="cta-button"  
                                                    onClick={() => toggleAllocation(purchase.id)} 
                                                > 
                                                    {buttonText} 
                                                </button> 
    
                                            </div> 
                                        </div> 
                                        {activePurchaseId === purchase.id && ( 
                                            <> 
                                                <hr className="divider" /> 
                                                <PlayerAllocation purchase={purchase} userData={userDashboardData} /> 
                                            </> 
                                        )} 
                                    </div> 
                                );
                            })} 
                        </div> 
                    )} 
                </div> 
            </div> 
        </div> 
    ); 
} 

export default Dashboard;